// Product page lightbox: PhotoSwipe 5.4.4 (photoswipe.com, MIT, assets/photoswipe.umd.min.js) opens one picture
// at a time fitted to the screen, with arrows, a counter and dots. Rendered by snippets/product-lightbox.liquid
// when main-product's "Image zoom" setting is "Open lightbox"; it takes the gallery clicks that would open
// Dawn's stacked media modal, which stays as the fallback if PhotoSwipe cannot load.
if (!customElements.get('product-lightbox')) {
  const VISIBLE_DOTS = 9;

  const loadAsset = (tag, attributes) =>
    new Promise((resolve, reject) => {
      const element = Object.assign(document.createElement(tag), attributes);
      element.onload = resolve;
      element.onerror = reject;
      document.head.appendChild(element);
    });

  const isInViewport = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  };

  const arrowSVG =
    '<svg class="pswp__icn" aria-hidden="true" width="28" height="28" viewBox="0 0 28 28"><path d="M18.5 3 7.5 14l11 11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const closeSVG =
    '<svg class="pswp__icn" aria-hidden="true" width="16" height="16" viewBox="0 0 16 16"><path d="m1.5 1.5 13 13m0-13-13 13" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';

  customElements.define(
    'product-lightbox',
    class ProductLightbox extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.load = this.load.bind(this);
      }

      connectedCallback() {
        this.gallery = document.getElementById(this.dataset.gallery);
        if (!this.gallery) return;
        const items = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
        this.images = new Map(items.map((item) => [String(item.id), item]));
        // Capture phase, so the click is handled before Dawn's modal-opener sees it.
        this.gallery.addEventListener('click', this.onClick, true);
        this.gallery.addEventListener('pointerover', this.load, { once: true, passive: true });
        // Fetch PhotoSwipe once the page is idle so the first click opens at once.
        const preload = () => this.load().catch(() => {});
        if ('requestIdleCallback' in window) requestIdleCallback(preload, { timeout: 4000 });
        else setTimeout(preload, 2000);
      }

      disconnectedCallback() {
        if (!this.gallery) return;
        this.gallery.removeEventListener('click', this.onClick, true);
        this.gallery.removeEventListener('pointerover', this.load);
      }

      onClick(event) {
        const toggle = event.target.closest('.product__media-toggle');
        if (!toggle || !this.images.has(toggle.dataset.mediaId)) return;
        event.preventDefault();
        event.stopPropagation();
        this.open(toggle);
      }

      load() {
        if (!this.loading) {
          this.loading = Promise.all([
            window.PhotoSwipe ? null : loadAsset('script', { src: this.dataset.script }),
            // Every product-lightbox.css rule is scoped to .pswp.product-lightbox, so load order does not matter.
            loadAsset('link', { rel: 'stylesheet', href: this.dataset.photoswipeStyles }),
            loadAsset('link', { rel: 'stylesheet', href: this.dataset.styles }),
          ]);
          this.loading.catch(() => (this.loading = null));
        }
        return this.loading;
      }

      async open(toggle) {
        const spinner = toggle.closest('.product__modal-opener').querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
        try {
          await this.load();
        } catch (error) {
          const modal = document.querySelector(toggle.closest('modal-opener')?.dataset.modal);
          if (modal) modal.show(toggle);
          return;
        } finally {
          if (spinner) spinner.classList.add('hidden');
        }

        const toggles = Array.from(this.gallery.querySelectorAll('.product__media-list .product__media-toggle')).filter(
          (element) => this.images.has(element.dataset.mediaId) && element.getClientRects().length > 0
        );
        const dataSource = toggles.map((element) => {
          const image = this.images.get(element.dataset.mediaId);
          const thumbnail = element.closest('.product__modal-opener').querySelector('.product__media img');
          return {
            src: image.src,
            width: image.width,
            height: image.height,
            alt: image.alt || '',
            msrc: (thumbnail && thumbnail.currentSrc) || undefined,
            element: thumbnail || undefined,
          };
        });

        const pswp = new window.PhotoSwipe({
          dataSource,
          index: Math.max(0, toggles.indexOf(toggle)),
          mainClass: 'product-lightbox',
          bgOpacity: 1,
          zoom: false,
          imageClickAction: 'zoom',
          paddingFn: (viewport) =>
            viewport.x < 750 ? { top: 56, bottom: 56, left: 0, right: 0 } : { top: 60, bottom: 64, left: 80, right: 80 },
          // Click zooms to at least twice the fitted size; tall strips zoom to the full screen width.
          secondaryZoomLevel: (zoom) => Math.max(Math.min(1, zoom.fit * 3), zoom.fill, zoom.fit * 2),
          maxZoomLevel: (zoom) => Math.max(zoom.fit * 4, 2),
          arrowPrevSVG: arrowSVG,
          arrowNextSVG: arrowSVG,
          closeSVG: closeSVG,
          arrowPrevTitle: this.dataset.labelPrevious,
          arrowNextTitle: this.dataset.labelNext,
          closeTitle: this.dataset.labelClose,
        });

        // Zoom back to the gallery picture only when it is on screen, otherwise fade.
        pswp.addFilter('thumbBounds', (bounds, data) => (isInViewport(data.element) ? bounds : undefined));
        pswp.on('uiRegister', () => this.registerDots(pswp));
        pswp.init();
        if (pswp.element) pswp.element.setAttribute('aria-label', this.dataset.labelDialog);
      }

      registerDots(pswp) {
        const dotLabel = this.dataset.labelDot;
        pswp.ui.registerElement({
          name: 'dots',
          appendTo: 'wrapper',
          order: 9,
          onInit: (element) => {
            const count = pswp.getNumItems();
            if (count < 2) return element.remove();

            const visible = Math.min(count, VISIBLE_DOTS);
            const track = document.createElement('div');
            track.className = 'pswp__dots-track';
            element.style.setProperty('--dots-visible', visible);
            const dots = Array.from({ length: count }, (_, index) => {
              const dot = document.createElement('button');
              dot.type = 'button';
              dot.className = 'pswp__dot';
              dot.setAttribute('aria-label', dotLabel.replace('__index__', index + 1));
              dot.addEventListener('click', () => pswp.goTo(index));
              track.appendChild(dot);
              return dot;
            });
            element.appendChild(track);

            const update = () => {
              const current = pswp.currIndex;
              const first = Math.min(Math.max(current - Math.floor(visible / 2), 0), count - visible);
              const last = first + visible - 1;
              track.style.setProperty('--dots-offset', first);
              dots.forEach((dot, index) => {
                const shown = index >= first && index <= last;
                dot.tabIndex = shown ? 0 : -1;
                dot.classList.toggle('is-active', index === current);
                if (index === current) dot.setAttribute('aria-current', 'true');
                else dot.removeAttribute('aria-current');
                // The edge dots shrink when more pictures lie beyond them.
                let size = '';
                if ((index === first && first > 0) || (index === last && last < count - 1)) size = 'small';
                else if ((index === first + 1 && first > 0) || (index === last - 1 && last < count - 1)) size = 'medium';
                dot.dataset.size = size;
              });
            };
            pswp.on('change', update);
            update();
          },
        });
      }
    }
  );
}
